import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import type { MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto';
import type { PlaylistMediaRow } from '@/helpers/flow-media-playlist.helper';
import { CollapsibleGroup } from '@/components/common/collapsible-group';
import { Divider } from '@mui/material';
import { MediaItem } from './media-item';
import { SortablePlaylistMediaList } from './sortable-playlist-media-list';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import { PLAY_TYPE } from '@/types/dtos/player/request/control-device.dto';
import { resolvePlayTypeForCompositeSlug } from '@/helpers/composite-slug.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { getObjectId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import { useGetPlayersQuery } from '@/state/queries/players/use-get-players';
import PlaylistVirtualMediaRow from '@/components/media/playlist-virtual-media-row';
import { useMemo, useEffect, useCallback } from 'react';
import type { MediaListDisplayVariant } from '@/components/flows/detail/flow-detail.types';
import { hasSlideSupport } from '@/constants/media-types';
import { useLocalMediaPreviewSlideStore } from '@/state/stores/use-local-media-preview-slide-store';
import { usePlayerSocket } from '@/hooks/use-player-socket';
import { useQueryClient } from '@tanstack/react-query';
import { GET_PLAYERS_QUERY_KEY } from '@/state/queries/players/use-get-players';
import { GET_LIVE_CONTEXT_STATE_QUERY_KEY } from '@/state/queries/player/use-get-live-context-state';
import { useGetLiveContextStateQuery } from '@/state/queries/player/use-get-live-context-state';
import { useGetSequenceIndexStatusQuery } from '@/state/queries/player/use-get-sequence-index-status';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { FlowControlStagingProvider } from './flow-control-staging.context';
import { useFlowControlLiveState } from '@/components/flows/detail/flow-control-live-state';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import { liveCueRowMatchesControlPlaylist } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { useGetCompositeComponentsQuery } from '@/state/queries/composite-components/use-get-composite-components';
import type { AllowedInContext } from '@/state/queries/composite-components/use-get-composite-components';
import { compareApplicableVirtualMediaByScope } from '@/helpers/virtual-media-scope-sort.helper';
import { useMediaProcessingSocketSync } from '@/hooks/use-media-processing-socket-sync';

export type VirtualMediaData = {
  player: PlayerDto;
};

export type VirtualMediaItem = {
  id: string;
  title: string;
  isVirtual: true;
  playType: (typeof PLAY_TYPE)[keyof typeof PLAY_TYPE];
  data?: VirtualMediaData;
  disabled?: boolean;
  entityType: 'Flow' | 'Session' | 'Event' | 'Break';
  entityId: string;
  contextEntityId?: string;
  contextEntityType?: MediaParentEntity;
};

export type MediaListContext = 'Flow' | 'Session' | 'Break' | 'Event';

type MediaItemUnion = MediaDto | VirtualMediaItem;

function mediaHasApprovedAsset(item: MediaDto): boolean {
  return item.assets?.some((asset) => asset.isApproved) ?? false;
}

export const MediaList = ({
  media,
  playlist,
  playlistRows,
  parentId,
  parentType,
  entityId,
  contextParentId,
  contextParentType,
  context,
  enablePlayerSocket = false,
  disablePlayerQuery = false,
  enabled = true,
  canReorderPlaylist,
  displayVariant = 'default',
  playlistScope,
  playlistSlotId,
  contributorFacing = false,
}: {
  media: MediaDto[];
  playlist?: MediaPlaylistDto;
  playlistRows?: PlaylistMediaRow[];
  parentId: string;
  parentType: MediaParentEntity;
  contextParentId: string;
  contextParentType: MediaParentEntity;
  entityId?: string;
  context: MediaListContext;
  liveReady?: boolean;
  enablePlayerSocket?: boolean;
  disablePlayerQuery?: boolean;
  enabled?: boolean;
  canReorderPlaylist?: boolean;
  displayVariant?: MediaListDisplayVariant;
  playlistScope?: string;
  playlistSlotId?: string;
  contributorFacing?: boolean;
}) => {
  useMediaProcessingSocketSync(parentType, parentId);
  const isFlowControl = displayVariant === 'flowControl';
  const liveState = useFlowControlLiveState();
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const liveReady = flow?.liveContext?.isRunning ?? false;
  // Per-row slide controls live in Perform/Control previews now — never show on playlist rows.
  const hidePerRowSlideControl = true;
  const event = useEventStore((state) => state.event);
  const eventId = event?.origin ? getObjectId(event.origin) : event?.id;
  const playlistSlotContentType = useMemo(() => {
    if (playlistScope !== 'slot' || !playlistSlotId) {
      return undefined;
    }
    for (const source of [flowPlan, flow]) {
      const slot = source?.slots?.find((entry) => entry.id === playlistSlotId);
      if (slot?.contentType) {
        return slot.contentType;
      }
    }
    return undefined;
  }, [flow, flowPlan, playlistScope, playlistSlotId]);
  const compositeQueryContext = useMemo((): AllowedInContext | undefined => {
    if (playlistScope === 'slot') {
      return playlistSlotContentType === 'Break' ? 'break' : 'flow';
    }
    if (playlistScope === 'session') {
      return 'session';
    }
    if (playlistScope) {
      return 'flow';
    }
    return undefined;
  }, [playlistScope, playlistSlotContentType]);
  const { data: compositeCatalog = [] } = useGetCompositeComponentsQuery(compositeQueryContext);
  const compositesBySlug = useMemo(
    () => new Map(compositeCatalog.map((composite) => [composite.slug, composite])),
    [compositeCatalog],
  );
  const mode = useEventMode();
  const queryClient = useQueryClient();
  const { data: players } = useGetPlayersQuery(
    disablePlayerQuery ? undefined : { entityId: contextParentId, entityType: contextParentType },
    { enabled: enabled && !disablePlayerQuery },
  );

  const entityRef = useMemo(() => {
    if (contextParentType === 'Flow') {
      return getEntityRefFromFlow(contextParentId, flow?.liveContext);
    }
    if (contextParentType === 'Event') {
      return `Event:${contextParentId}`;
    }
    return undefined;
  }, [contextParentId, contextParentType, flow?.liveContext]);
  const liveSheetRoomKey = entityRoomKeyFromEntityRef(entityRef ?? '');
  const liveCueSheet = usePerformControlWsStore((s) =>
    liveSheetRoomKey ? s.cueSheetByRoom[liveSheetRoomKey] : undefined,
  );
  const liveSheetItemIds = useMemo(() => {
    if (!isFlowControl || !liveReady || !Array.isArray(liveCueSheet?.items)) {
      return undefined;
    }
    return liveCueSheet.items
      .filter(
        (row): row is NonNullable<typeof row> & { itemId: string } => Boolean(row?.itemId) && !row?.flags?.detached,
      )
      .filter((row) => liveCueRowMatchesControlPlaylist(row, playlistScope, playlistSlotId))
      .map((row) => row.itemId);
  }, [isFlowControl, liveCueSheet, liveReady, playlistScope, playlistSlotId]);
  const filteredMedia = useMemo(() => {
    const approved = isFlowControl ? media.filter(mediaHasApprovedAsset) : media;
    if (!liveSheetItemIds) {
      return approved;
    }
    const byId = new Map(approved.map((item) => [item.id, item]));
    const ordered: typeof approved = [];
    for (const itemId of liveSheetItemIds) {
      const bare = itemId.includes(':') ? itemId.slice(itemId.indexOf(':') + 1) : itemId;
      const found = byId.get(bare) ?? byId.get(itemId);
      if (found && !ordered.includes(found)) {
        ordered.push(found);
      }
    }
    return ordered;
  }, [isFlowControl, liveSheetItemIds, media]);
  const filteredPlaylistRows = useMemo(() => {
    const approved =
      isFlowControl && playlistRows ? playlistRows.filter((row) => mediaHasApprovedAsset(row.media)) : playlistRows;
    if (!liveSheetItemIds || !approved) {
      return approved;
    }
    const byId = new Map(approved.map((row) => [row.media.id, row]));
    const ordered: typeof approved = [];
    for (const itemId of liveSheetItemIds) {
      const bare = itemId.includes(':') ? itemId.slice(itemId.indexOf(':') + 1) : itemId;
      const found = byId.get(bare) ?? byId.get(itemId);
      if (found && !ordered.includes(found)) {
        ordered.push(found);
      }
    }
    return ordered;
  }, [isFlowControl, liveSheetItemIds, playlistRows]);
  // Control tab already owns the entity socket + live-state GET. Per-list joins crash on go-live.
  const shouldEnablePlayerSocket = enablePlayerSocket && mode === 'PERFORM' && !isFlowControl;
  const socketEntityId =
    isFlowControl && liveReady && flow?.liveContext?.entityId ? flow.liveContext.entityId : contextParentId;
  const socketEntityType =
    isFlowControl && liveReady && flow?.liveContext?.entityType
      ? (flow.liveContext.entityType as MediaParentEntity)
      : contextParentType;

  // Listen for WebSocket events to invalidate player cache when status updates occur
  usePlayerSocket(
    socketEntityId,
    socketEntityType,
    {
      onPlayersInvalidated: () => {
        queryClient.invalidateQueries({ queryKey: [GET_PLAYERS_QUERY_KEY] });
      },
      ...(isFlowControl && entityRef
        ? {
            onLiveContextInvalidated: () => {
              queryClient.invalidateQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
            },
          }
        : {}),
    },
    shouldEnablePlayerSocket,
  );
  const playersPlayingType = useMemo(() => {
    if (!Array.isArray(players)) return undefined;
    return players.find((player) => player.isConnected)?.state?.page;
  }, [players]);

  const connectedPlayer = useMemo(() => {
    if (!Array.isArray(players)) return undefined;
    // Prefer media-agent player that has slide information, otherwise fall back to first connected player
    const mediaAgentPlayer = players.find(
      (player) =>
        player.isConnected &&
        player.type === 'mediaAgent' &&
        (player.currentSlideIndex !== undefined || player.totalSlides !== undefined),
    );
    return mediaAgentPlayer || players.find((player) => player.isConnected);
  }, [players]);

  const liveContextQueryEnabled = enabled && !!entityRef && mode === 'PERFORM' && !isFlowControl;
  const { data: liveContextStateFromQuery, dataUpdatedAt: liveContextQueryUpdatedAtFromQuery } =
    useGetLiveContextStateQuery(entityRef, {
      enabled: liveContextQueryEnabled,
    });
  const { data: sequenceIndexStatus } = useGetSequenceIndexStatusQuery(entityRef, {
    enabled: liveContextQueryEnabled,
  });
  const liveContextState = isFlowControl ? liveState.liveContextState : liveContextStateFromQuery;
  const liveContextQueryUpdatedAt = isFlowControl
    ? liveState.liveContextQueryUpdatedAt
    : liveContextQueryUpdatedAtFromQuery;

  const stepLocalPreviewSlide = useLocalMediaPreviewSlideStore((s) => s.stepSlide);

  // Get currently selected media item with slide support.
  // Fallback order: media-agent state first, then live-context playState.
  const selectedMediaWithSlides = useMemo(() => {
    if (connectedPlayer?.state) {
      const state = connectedPlayer.state;
      const playType = state.page;

      if (playType === PLAY_TYPE.MEDIA) {
        const statePayload = (state as { payload?: { media?: { id?: string } } }).payload;
        const stateMediaId = statePayload?.media?.id;
        if (stateMediaId) {
          const mediaItem = media.find((m) => m.id === stateMediaId);
          if (mediaItem) {
            const latestApprovedVersion = mediaItem.assets
              ?.filter((asset) => asset.isApproved)
              ?.sort((a, b) => b.version - a.version)?.[0];
            if (latestApprovedVersion) {
              const hasSlides =
                (latestApprovedVersion.fileType && hasSlideSupport(latestApprovedVersion.fileType)) ||
                latestApprovedVersion.name.toLowerCase().match(/\.(pptx?|pdf)$/);
              if (hasSlides) {
                return {
                  mediaId: mediaItem.id,
                  entityType: contextParentType,
                  entityId: contextParentId,
                };
              }
            }
          }
        }
      }
    }

    if (liveContextState?.stagedSelection?.focusedItemId?.startsWith('media:')) {
      const mediaId = liveContextState.stagedSelection.focusedItemId.slice(6);
      const mediaItem = media.find((m) => m.id === mediaId);
      if (mediaItem) {
        const latestApprovedVersion = mediaItem.assets
          ?.filter((asset) => asset.isApproved)
          ?.sort((a, b) => b.version - a.version)?.[0];
        if (latestApprovedVersion) {
          const hasSlides =
            (latestApprovedVersion.fileType && hasSlideSupport(latestApprovedVersion.fileType)) ||
            latestApprovedVersion.name.toLowerCase().match(/\.(pptx?|pdf)$/);
          if (hasSlides) {
            return {
              mediaId: mediaItem.id,
              entityType: contextParentType,
              entityId: contextParentId,
            };
          }
        }
      }
    }

    const playedItemIds = liveContextState?.playState?.playedItemIds;
    if (playedItemIds?.length) {
      const firstMediaItemId = playedItemIds.find((e) => e.itemId?.startsWith('media:'))?.itemId;
      if (firstMediaItemId) {
        const mediaId = firstMediaItemId.slice(6);
        const mediaItem = media.find((m) => m.id === mediaId);
        if (mediaItem) {
          const latestApprovedVersion = mediaItem.assets
            ?.filter((asset) => asset.isApproved)
            ?.sort((a, b) => b.version - a.version)?.[0];
          if (latestApprovedVersion) {
            const hasSlides =
              (latestApprovedVersion.fileType && hasSlideSupport(latestApprovedVersion.fileType)) ||
              latestApprovedVersion.name.toLowerCase().match(/\.(pptx?|pdf)$/);
            if (hasSlides) {
              return {
                mediaId: mediaItem.id,
                entityType: contextParentType,
                entityId: contextParentId,
              };
            }
          }
        }
      }
    }

    return null;
  }, [connectedPlayer, liveContextState, media, contextParentType, contextParentId]);

  // Keyboard shortcuts for local asset preview slide navigation (does not affect live playout).
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!selectedMediaWithSlides || isFlowControl) return;

      // Ignore if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepLocalPreviewSlide(selectedMediaWithSlides.mediaId, -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepLocalPreviewSlide(selectedMediaWithSlides.mediaId, 1);
      }
    },
    [selectedMediaWithSlides, isFlowControl, stepLocalPreviewSlide],
  );

  useEffect(() => {
    if (!selectedMediaWithSlides) return;

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedMediaWithSlides, handleKeyDown]);

  const isItemSelected = (item: MediaItemUnion): boolean => {
    // For real media items: also consider live-context selection (focusedItemId/playedItemIds)
    // when connected player is layer-based (state.page === 'live') and has no media-agent format
    if (!(item as VirtualMediaItem).isVirtual && selectedMediaWithSlides?.mediaId === item.id) {
      return true;
    }

    if (!connectedPlayer?.state) return false;

    const state = connectedPlayer.state;
    const playType = state.page;

    // For virtual items (non-media)
    if ((item as VirtualMediaItem).isVirtual) {
      const virtualItem = item as VirtualMediaItem;

      // For LIVE type, match by playType and playerId
      if (virtualItem.playType === PLAY_TYPE.LIVE) {
        const statePlayerId = (state as { playerId?: string }).playerId;
        return playType === PLAY_TYPE.LIVE && statePlayerId === virtualItem.data?.player?.id;
      }

      // For other virtual items, match by playType only
      return playType === virtualItem.playType;
    }

    // For real media items, match by playType=MEDIA and media.id (media-agent format)
    const statePayload = (state as { payload?: { media?: { id?: string; parent?: { id?: string } } } }).payload;
    const stateMediaId = statePayload?.media?.id;

    const isSelected = playType === PLAY_TYPE.MEDIA && stateMediaId === item.id;
    return isSelected;
  };

  type VirtualMediaRowConfig = {
    slug: string;
    name: string;
    playType: (typeof PLAY_TYPE)[keyof typeof PLAY_TYPE];
    entityType: 'Flow' | 'Session' | 'Event' | 'Break';
    entityId: string;
  };

  /** Virtual media rows from playlist applicableVirtualMedia (settings + playback on one line). */
  const getVirtualMediaRows = (): { start: VirtualMediaRowConfig[]; end: VirtualMediaRowConfig[] } => {
    const applicable = playlist?.applicableVirtualMedia ?? [];
    if (!playlist || applicable.length === 0) {
      return { start: [], end: [] };
    }

    const canEdit = playlist.virtualMediaCapabilities?.canEdit ?? false;
    const canManage = playlist.permissions?.includes('manage') ?? false;
    const canToggleEnabled = playlist.virtualMediaCapabilities?.canToggleEnabled ?? canEdit;
    const enabledSlugs = new Set(playlist.effectiveVirtualMedia?.enabledSlugs ?? []);
    if (!isFlowControl && !canEdit && !canManage && enabledSlugs.size === 0) {
      return { start: [], end: [] };
    }

    // Assets: show all when editing. Perform: show skipped too so advance include/skip can re-enable.
    const showAllApplicable = isFlowControl ? canToggleEnabled || canManage : canEdit || canManage;
    const visible = showAllApplicable ? applicable : applicable.filter((item) => enabledSlugs.has(item.slug));
    const defaultEntityType =
      context === 'Event' ? 'Event' : context === 'Flow' ? 'Flow' : context === 'Session' ? 'Session' : 'Break';
    const defaultEntityId =
      context === 'Event' ? (eventId ?? parentId) : context === 'Session' ? parentId : (entityId ?? parentId);
    const resolvedSlotId = playlistSlotId ?? playlist.slotId;

    const toRow = (item: (typeof applicable)[number]): VirtualMediaRowConfig => {
      const playType = resolvePlayTypeForCompositeSlug(item.slug);
      // break-info must target the Break slot, even when listed under a Flow playlist widget.
      if (playType === PLAY_TYPE.BREAK_INFO && resolvedSlotId) {
        return {
          slug: item.slug,
          name: item.name,
          playType,
          entityType: 'Break',
          entityId: resolvedSlotId,
        };
      }
      return {
        slug: item.slug,
        name: item.name,
        playType,
        entityType: defaultEntityType,
        entityId: defaultEntityId,
      };
    };

    const scopeForSort = playlistScope ?? playlist.scope;
    const sortRows = (items: typeof visible) =>
      [...items].sort((a, b) =>
        compareApplicableVirtualMediaByScope(a, b, scopeForSort, playlistSlotContentType, compositesBySlug),
      );

    const start = sortRows(visible.filter((item) => !item.isClosing)).map(toRow);
    const end = sortRows(visible.filter((item) => item.isClosing)).map(toRow);

    return { start, end };
  };

  const virtualRows = getVirtualMediaRows();

  const isVirtualSlugSelected = (_slug: string, playType: (typeof PLAY_TYPE)[keyof typeof PLAY_TYPE]): boolean => {
    if (!connectedPlayer?.state) {
      return false;
    }

    const state = connectedPlayer.state;
    if (playType === PLAY_TYPE.LIVE) {
      const statePlayerId = (state as { playerId?: string }).playerId;
      return state.page === PLAY_TYPE.LIVE && !!statePlayerId;
    }

    return state.page === playType;
  };

  const renderVirtualMediaRow = (row: VirtualMediaRowConfig) => (
    <PlaylistVirtualMediaRow
      key={`${playlistSlotId ?? playlist!.id}-${row.slug}`}
      playlist={playlist!}
      slug={row.slug}
      name={row.name}
      playType={row.playType}
      entityType={row.entityType}
      entityId={row.entityId}
      contextParentId={contextParentId}
      contextParentType={contextParentType}
      selected={isVirtualSlugSelected(row.slug, row.playType)}
      playersPlayingType={playersPlayingType}
      displayVariant={displayVariant}
      playlistScope={playlistScope ?? playlist!.scope}
      playlistSlotId={playlistSlotId ?? playlist!.slotId}
      contributorFacing={contributorFacing}
    />
  );

  const useSortablePlaylist = Boolean(
    playlist?.id && filteredPlaylistRows && filteredPlaylistRows.length > 0 && !isFlowControl,
  );
  const hasFlowControlPlaylistContent =
    (filteredPlaylistRows?.length ?? 0) > 0 || virtualRows.start.length > 0 || virtualRows.end.length > 0;
  const useFlowControlPlaylist = Boolean(isFlowControl && playlist?.id && hasFlowControlPlaylistContent);
  // Control: only the session↔transition separator is a divider; virtual/real stay one list.
  const showVirtualDividers = !isFlowControl && (virtualRows.start.length > 0 || virtualRows.end.length > 0);

  const playlistCanReorder =
    canReorderPlaylist ??
    (playlist?.permissions?.includes('reorder') || playlist?.permissions?.includes('manage') || false);

  // Advance / auto-loop / next-show-mode require MediaPlaylist manage.
  // Hide in contributor-facing views (e.g. role-details) so managers mirror the contributor UI.
  const canEditPlaylistAdvanceMode = !contributorFacing && Boolean(playlist?.permissions?.includes('manage'));

  const renderStandardItem = (item: MediaItemUnion) => (
    <MediaItem
      key={item.id}
      selected={isItemSelected(item)}
      mediaItem={item}
      parentId={(item as VirtualMediaItem)?.contextEntityId ?? parentId}
      parentType={(item as VirtualMediaItem)?.contextEntityType ?? parentType}
      currentSlideIndex={connectedPlayer?.currentSlideIndex ?? sequenceIndexStatus?.index}
      totalSlides={connectedPlayer?.totalSlides ?? sequenceIndexStatus?.frameCount}
      speakerNotes={connectedPlayer?.speakerNotes}
      contextParentId={contextParentId}
      contextParentType={contextParentType}
      playersPlayingType={playersPlayingType}
      displayVariant={displayVariant}
      hidePerRowSlideControl={hidePerRowSlideControl}
      contributorFacing={contributorFacing}
    />
  );

  const renderPlaylistRowItem = (row: PlaylistMediaRow) => (
    <MediaItem
      key={row.usageId}
      selected={isItemSelected(row.media)}
      mediaItem={row.media}
      parentId={parentId}
      parentType={parentType}
      currentSlideIndex={connectedPlayer?.currentSlideIndex ?? sequenceIndexStatus?.index}
      totalSlides={connectedPlayer?.totalSlides ?? sequenceIndexStatus?.frameCount}
      speakerNotes={connectedPlayer?.speakerNotes}
      contextParentId={contextParentId}
      contextParentType={contextParentType}
      playersPlayingType={playersPlayingType}
      playlistId={playlist!.id}
      playlistScope={playlistScope ?? playlist!.scope}
      playlistSlotId={playlistSlotId ?? playlist!.slotId}
      playlistUsageId={row.usageId}
      playlistAdvanceMode={row.advanceMode}
      playlistNextShowMode={row.nextShowMode}
      playlistLoopEnabled={row.loopEnabled}
      playlistDwellMs={row.dwellMs}
      canEditPlaylistAdvanceMode={canEditPlaylistAdvanceMode}
      displayVariant={displayVariant}
      hidePerRowSlideControl={hidePerRowSlideControl}
      contributorFacing={contributorFacing}
    />
  );

  const listContent = (
    <CollapsibleGroup>
      {useFlowControlPlaylist ? (
        <>
          {virtualRows.start.map((row) => renderVirtualMediaRow(row))}
          {showVirtualDividers && virtualRows.start.length > 0 && filteredPlaylistRows!.length > 0 ? (
            <Divider sx={{ my: 1 }} />
          ) : null}
          {filteredPlaylistRows!.map((row) => renderPlaylistRowItem(row))}
          {showVirtualDividers &&
          virtualRows.end.length > 0 &&
          (virtualRows.start.length > 0 || filteredPlaylistRows!.length > 0) ? (
            <Divider sx={{ my: 1 }} />
          ) : null}
          {virtualRows.end.map((row) => renderVirtualMediaRow(row))}
        </>
      ) : useSortablePlaylist ? (
        <>
          {virtualRows.start.map((row) => renderVirtualMediaRow(row))}
          {showVirtualDividers && virtualRows.start.length > 0 && filteredMedia.length > 0 ? (
            <Divider sx={{ my: 1 }} />
          ) : null}
          <SortablePlaylistMediaList
            rows={filteredPlaylistRows!}
            playlistId={playlist!.id}
            canReorder={playlistCanReorder}
            canEditPlaylistAdvanceMode={canEditPlaylistAdvanceMode}
            parentId={parentId}
            parentType={parentType}
            contextParentId={contextParentId}
            contextParentType={contextParentType}
            playersPlayingType={playersPlayingType}
            connectedPlayer={connectedPlayer}
            isItemSelected={(item) => isItemSelected(item)}
            displayVariant={displayVariant}
            hidePerRowSlideControl={hidePerRowSlideControl}
            contributorFacing={contributorFacing}
          />
          {showVirtualDividers &&
          virtualRows.end.length > 0 &&
          (virtualRows.start.length > 0 || filteredMedia.length > 0) ? (
            <Divider sx={{ my: 1 }} />
          ) : null}
          {virtualRows.end.map((row) => renderVirtualMediaRow(row))}
        </>
      ) : showVirtualDividers ? (
        <>
          {virtualRows.start.map((row) => renderVirtualMediaRow(row))}
          {virtualRows.start.length > 0 && filteredMedia.length > 0 ? <Divider sx={{ my: 1 }} /> : null}
          {filteredMedia.map((item) => renderStandardItem(item))}
          {(virtualRows.start.length > 0 || filteredMedia.length > 0) && virtualRows.end.length > 0 ? (
            <Divider sx={{ my: 1 }} />
          ) : null}
          {virtualRows.end.map((row) => renderVirtualMediaRow(row))}
        </>
      ) : (
        filteredMedia.map((item) => renderStandardItem(item))
      )}
    </CollapsibleGroup>
  );

  if (isFlowControl) {
    return (
      <FlowControlStagingProvider
        entityRef={entityRef ?? liveState.entityRef}
        liveContextState={liveContextState}
        liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
        liveReady={liveReady}
      >
        {listContent}
      </FlowControlStagingProvider>
    );
  }

  return listContent;
};
